import {
  Box,
  Card,
  Divider,
  Stack,
  Typography,
  alpha,
} from '@mui/material';
import React from 'react';
import AccountBalanceWalletOutlinedIcon from '@mui/icons-material/AccountBalanceWalletOutlined';

const iconData = {
  other: {
    src: '/Deposit.svg',
    alt: 'Transaction',
    bgColor: '#E7EDFF',
  },
};

const ListCard = ({ transactions }) => {
  const items =
    transactions?.length > 0 ? transactions.slice(0, 5) : [];

  const isFailedOrExpired = (transaction) => {
    const status = (
      transaction?.status ??
      transaction?.transactionStatus ??
      transaction?.orderStatus ??
      ''
    ).toLowerCase();
    return status === 'failed' || status === 'expired';
  };

  const getAmount = (transaction) => {
    const raw =
      transaction?.orderAmount ??
      transaction?.amount ??
      transaction?.order?.amount;
    if (raw == null || raw === '') return 0;
    const num = Number(raw);
    return Number.isFinite(num) ? num : 0;
  };

  const formatAmount = (transaction) => {
    const amount = getAmount(transaction);
    return `₹${amount.toLocaleString('en-IN')}`;
  };

  const getDisplayStatus = (transaction) => {
    const status =
      transaction?.orderStatus ??
      transaction?.status ??
      transaction?.transactionStatus ??
      '';
    if (!status) return '—';
    const normalized = String(status).toUpperCase();
    if (normalized === 'PROCCESSED') return 'PROCESSED';
    return normalized;
  };

  const getStatusColor = (transaction) => {
    const status = (
      transaction?.status ??
      transaction?.transactionStatus ??
      transaction?.orderStatus ??
      ''
    ).toLowerCase();
    if (status === 'failed' || status === 'expired') return '#dc2626';
    if (status === 'success' || status === 'paid') return '#16a34a';
    if (status === 'pending' || status === 'pending_payment') return '#d97706';
    return '#64748b';
  };

  const getStatusBg = (transaction) => {
    const color = getStatusColor(transaction);
    return alpha(color, 0.12);
  };

  return (
    <Card
      elevation={0}
      sx={{
        height: { xs: 360, md: 420 },
        borderRadius: '24px',
        border: '1px solid',
        borderColor: alpha('#11409B', 0.08),
        bgcolor: '#fff',
        boxShadow: '0 4px 32px rgba(15, 23, 42, 0.06)',
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
      }}
    >
      <Box sx={{ px: 2.5, pt: 2.5, pb: 1 }}>
        <Typography variant="caption" sx={{ color: '#64748b', fontWeight: 500 }}>
          {items.length} recent
        </Typography>
      </Box>

      <Box sx={{ flex: 1, overflow: 'auto', px: 1.5, pb: 1.5 }}>
        {items.length === 0 ? (
          <Stack
            alignItems="center"
            justifyContent="center"
            sx={{ height: '100%', py: 6, px: 2 }}
          >
            <AccountBalanceWalletOutlinedIcon
              sx={{ fontSize: 48, color: '#cbd5e1', mb: 1 }}
            />
            <Typography sx={{ color: '#94a3b8', textAlign: 'center' }}>
              No recent transactions
            </Typography>
          </Stack>
        ) : (
          items.map((transaction, index) => (
            <Box key={transaction?.id || transaction?._id || index}>
              <Stack
                direction="row"
                alignItems="center"
                spacing={1.5}
                sx={{
                  px: 1.5,
                  py: 1.75,
                  borderRadius: '14px',
                  transition: 'background 0.2s',
                  '&:hover': { bgcolor: '#f8fafc' },
                }}
              >
                <Box
                  sx={{
                    width: 44,
                    height: 44,
                    borderRadius: '12px',
                    bgcolor: iconData.other.bgColor,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexShrink: 0,
                  }}
                >
                  <Box
                    component="img"
                    src={iconData.other.src}
                    alt={iconData.other.alt}
                    sx={{ width: 22, height: 22 }}
                  />
                </Box>

                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography
                    variant="body2"
                    sx={{
                      fontWeight: 600,
                      color: '#0f172a',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {transaction?.userName ||
                      transaction?.userId ||
                      transaction?.customerDetails?.customerId?.username ||
                      '—'}
                  </Typography>
                  <Typography
                    variant="caption"
                    sx={{ color: '#94a3b8', display: 'block' }}
                  >
                    {transaction?.createdAt
                      ? new Date(transaction.createdAt).toLocaleDateString(
                          'en-IN',
                          {
                            day: 'numeric',
                            month: 'short',
                            year: 'numeric',
                          }
                        )
                      : '—'}
                  </Typography>
                  <Box
                    component="span"
                    sx={{
                      display: 'inline-block',
                      mt: 0.5,
                      px: 1,
                      py: 0.2,
                      borderRadius: '6px',
                      fontSize: '0.65rem',
                      fontWeight: 700,
                      letterSpacing: '0.04em',
                      color: getStatusColor(transaction),
                      bgcolor: getStatusBg(transaction),
                    }}
                  >
                    {getDisplayStatus(transaction)}
                  </Box>
                </Box>

                <Typography
                  sx={{
                    fontWeight: 700,
                    fontSize: '0.9rem',
                    color: isFailedOrExpired(transaction)
                      ? '#dc2626'
                      : '#0f172a',
                    flexShrink: 0,
                  }}
                >
                  {formatAmount(transaction)}
                </Typography>
              </Stack>
              {index < items.length - 1 && (
                <Divider sx={{ mx: 2, borderColor: '#f1f5f9' }} />
              )}
            </Box>
          ))
        )}
      </Box>
    </Card>
  );
};

export default ListCard;
